import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Copy,
  ExternalLink,
  Download,
  Loader2,
  Pencil,
  Save,
  X,
  Power,
  ArrowLeft,
  QrCode,
  Building2,
  MapPin,
  Link as LinkIcon,
  ScanLine,
  Clock,
  Sticker,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Info,
  Cpu,
  PackageCheck,
  TestTube2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/scans/customer/${id}`, { params: { limit: 25 } }),
      ]);
      setCustomer(c.data);
      setForm(c.data);
      setScans(s.data?.scans || []);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/kunden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(customer.nfc_link);
      toast.success("NFC-Link kopiert. Diesen Link jetzt in NFC Tools als URL/URI auf den Chip schreiben.");
      // Auto-tick the "nfc_copied" checklist item
      if (customer && !customer.activation_checklist?.nfc_copied) {
        toggleChecklist("nfc_copied", true);
      }
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const [nfcModalOpen, setNfcModalOpen] = useState(false);

  const toggleChecklist = async (key, value) => {
    try {
      const { data } = await api.patch(`/customers/${id}/activation`, { key, value });
      setCustomer(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadSticker = async () => {
    if (!customer) return;
    const url = `${API_BASE}/customers/${customer.id}/sticker.png`;
    const token = localStorage.getItem("1ai_token");
    try {
      const r = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(String(r.status));
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `1ai-review-sticker-${customer.slug}-${customer.stand_code}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Sticker-Download fehlgeschlagen.");
    }
  };

  const toggleStatus = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/customers/${id}`, {
        status: customer.status === "active" ? "inactive" : "active",
      });
      setCustomer(data);
      toast.success(data.status === "active" ? "Aktiviert." : "Deaktiviert.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const payload = {
        business_name: form.business_name,
        address: form.address,
        google_review_url: form.google_review_url,
        google_maps_url: form.google_maps_url,
        slug: form.slug,
      };
      const { data } = await api.patch(`/customers/${id}`, payload);
      setCustomer(data);
      setForm(data);
      setEditing(false);
      toast.success("Änderungen gespeichert.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const downloadQr = () => {
    if (!customer) return;
    const url = `${API_BASE}/customers/${customer.id}/qr.png?size=1200`;
    const token = localStorage.getItem("1ai_token");
    fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("HTTP " + r.status))))
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `1ai-review-${customer.slug}-${customer.stand_code}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(a.href);
        if (!customer.activation_checklist?.qr_downloaded) {
          toggleChecklist("qr_downloaded", true);
        }
      })
      .catch(() => toast.error("Download fehlgeschlagen."));
  };

  const testQr = () => {
    if (!customer) return;
    window.open(customer.nfc_link, "_blank", "noopener");
    toast.success("QR-Ziel geöffnet. Prüfe, dass Google-Bewertung erscheint.");
    if (!customer.activation_checklist?.qr_tested) {
      toggleChecklist("qr_tested", true);
    }
  };

  const testNfc = () => {
    if (!customer) return;
    window.open(customer.nfc_link, "_blank", "noopener");
    toast.success("NFC-Ziel geöffnet. Am Handy: Chip antippen und Ergebnis vergleichen.");
    if (!customer.activation_checklist?.nfc_tested) {
      toggleChecklist("nfc_tested", true);
    }
  };

  const nfcLink = customer?.nfc_link || "";

  if (loading || !customer) {
    return (
      <div className="text-muted-foreground text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/kunden"
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"
          data-testid="back-to-customers"
        >
          <ArrowLeft className="w-3 h-3" /> Zurück
        </Link>
        <div className="flex items-center gap-2">
          <LifecycleBadge status={customer.lifecycle_status} testId="detail-lifecycle-badge" />
          <Badge
            variant="outline"
            className={
              customer.status === "active"
                ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                : "border-white/10 text-muted-foreground"
            }
            data-testid="detail-status-badge"
          >
            {customer.status === "active" ? "Aktiv" : "Inaktiv"}
          </Badge>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <p className="label-eyebrow">Kunde</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 break-words">
            {customer.business_name}
          </h1>
          <div className="mt-1.5 text-sm text-muted-foreground flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="break-words">{customer.address}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            className="rounded-full gap-2"
            onClick={copyLink}
            data-testid="detail-copy-nfc-btn"
          >
            <Copy className="w-4 h-4" /> NFC-Link kopieren
          </Button>
          <a
            href={customer.google_review_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="detail-test-review-btn"
          >
            <Button variant="secondary" className="rounded-full gap-2">
              <ExternalLink className="w-4 h-4" /> Bewertungslink testen
            </Button>
          </a>
          <Button
            variant="ghost"
            className="rounded-full gap-2"
            onClick={toggleStatus}
            disabled={saving}
            data-testid="detail-toggle-status-btn"
          >
            <Power className="w-4 h-4" />
            {customer.status === "active" ? "Deaktivieren" : "Aktivieren"}
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-4">
        <SmallKpi
          label="Scans gesamt"
          value={customer.total_scans}
          icon={ScanLine}
          testId="detail-kpi-total"
        />
        <SmallKpi
          label="Scans heute"
          value={customer.scans_today}
          icon={ScanLine}
          tone="primary"
          testId="detail-kpi-today"
        />
        <SmallKpi
          label="Letzter Scan"
          value={customer.last_scan_at ? fmt(customer.last_scan_at) : "—"}
          icon={Clock}
          small
          testId="detail-kpi-last"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Data + Edit */}
        <div className="lg:col-span-2 space-y-6">
          {/* Aktiver Aufsteller-Link — canonical stand URL */}
          <div className="card-surface p-5 border-primary/20" data-testid="stand-link-card">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Sticker className="w-4 h-4 text-primary" />
                <h3 className="font-heading text-lg font-semibold">Aktiver Aufsteller-Link</h3>
              </div>
              <LifecycleBadge status={customer.lifecycle_status} />
            </div>
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-3 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
              QR-Code und NFC-Chip müssen exakt diesen Link verwenden.
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-3 text-sm">
              <div>
                <div className="label-eyebrow">Stand ID</div>
                <div className="font-mono text-primary text-lg mt-1" data-testid="stand-code">{customer.stand_code || "—"}</div>
              </div>
              <div>
                <div className="label-eyebrow">Firma</div>
                <div className="mt-1 truncate">{customer.business_name}</div>
              </div>
            </div>
            <div className="label-eyebrow mb-1">Vollständiger Aufsteller-Link</div>
            <div className="font-mono text-xs bg-white/[0.02] px-3 py-2 rounded border border-white/[0.05] break-all mb-3" data-testid="stand-url">
              {customer.nfc_link}
            </div>

            {/* Primary onboarding actions */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <Link to={`/kunden/${customer.id}/label`} data-testid="stand-open-label-btn">
                <Button size="sm" className="w-full rounded-full gap-1.5">
                  <Sticker className="w-3.5 h-3.5" /> Label &amp; Druck
                </Button>
              </Link>
              <Link to={`/kunden/${customer.id}/nfc`} data-testid="stand-open-nfc-btn">
                <Button size="sm" variant="secondary" className="w-full rounded-full gap-1.5">
                  <Cpu className="w-3.5 h-3.5" /> NFC programmieren
                </Button>
              </Link>
              <Button
                size="sm"
                variant="secondary"
                onClick={testQr}
                className="w-full rounded-full gap-1.5"
                data-testid="test-qr-btn"
              >
                <TestTube2 className="w-3.5 h-3.5" /> QR testen
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={testNfc}
                className="w-full rounded-full gap-1.5"
                data-testid="test-nfc-btn"
              >
                <TestTube2 className="w-3.5 h-3.5" /> NFC testen
              </Button>
            </div>

            {/* Secondary actions */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button size="sm" variant="ghost" onClick={copyLink} className="rounded-full gap-1.5" data-testid="stand-copy-nfc-btn">
                <Copy className="w-3.5 h-3.5" /> Link kopieren
              </Button>
              <Button size="sm" variant="ghost" onClick={downloadQr} className="rounded-full gap-1.5" data-testid="stand-download-qr-btn">
                <Download className="w-3.5 h-3.5" /> QR-PNG 1200 px
              </Button>
              <Button size="sm" variant="ghost" onClick={downloadSticker} className="rounded-full gap-1.5" data-testid="stand-download-sticker-btn">
                <Sticker className="w-3.5 h-3.5" /> 40×40 mm
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setNfcModalOpen(true)} className="rounded-full gap-1.5" data-testid="stand-nfc-howto-btn">
                <Info className="w-3.5 h-3.5" /> Kurz-Anleitung
              </Button>
            </div>

            <Separator className="bg-white/[0.06] my-3" />

            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow">Onboarding-Checkliste</div>
              <ChecklistProgress cl={customer.activation_checklist} />
            </div>
            <ul className="space-y-2" data-testid="activation-checklist">
              <ChecklistItem label="Kunde angelegt" done autoLocked testId="checklist-customer_created" />
              <ChecklistItem label="QR-Code generiert" done autoLocked testId="checklist-qr_generated" />
              <ChecklistItem
                label="QR gedruckt / heruntergeladen"
                done={!!customer.activation_checklist?.qr_downloaded}
                onChange={(v) => toggleChecklist("qr_downloaded", v)}
                testId="checklist-qr_downloaded"
              />
              <ChecklistItem
                label="NFC programmiert (Link geschrieben)"
                done={!!customer.activation_checklist?.nfc_copied}
                onChange={(v) => toggleChecklist("nfc_copied", v)}
                testId="checklist-nfc_copied"
              />
              <ChecklistItem
                label="QR getestet"
                done={!!customer.activation_checklist?.qr_tested}
                onChange={(v) => toggleChecklist("qr_tested", v)}
                testId="checklist-qr_tested"
              />
              <ChecklistItem
                label="NFC getestet"
                done={!!customer.activation_checklist?.nfc_tested}
                onChange={(v) => toggleChecklist("nfc_tested", v)}
                testId="checklist-nfc_tested"
              />
              <ChecklistItem
                label="An Kunden ausgeliefert"
                done={!!customer.activation_checklist?.delivered}
                onChange={(v) => toggleChecklist("delivered", v)}
                testId="checklist-delivered"
                icon={PackageCheck}
              />
            </ul>
            <p className="text-[11px] text-muted-foreground mt-3">
              &bdquo;Verkaufsbereit&ldquo; gilt bei allen vier Vorbereitungsschritten. &bdquo;Ausgeliefert&ldquo;
              schlie&szlig;t das Onboarding ab.
            </p>
          </div>

          <div className="card-surface p-5" data-testid="detail-info-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" />
                <h3 className="font-heading text-lg font-semibold">Firmendaten</h3>
              </div>
              {!editing ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(true)}
                  className="gap-1.5"
                  data-testid="detail-edit-btn"
                >
                  <Pencil className="w-3.5 h-3.5" /> Bearbeiten
                </Button>
              ) : (
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setForm(customer);
                      setEditing(false);
                    }}
                    className="gap-1.5"
                    data-testid="detail-edit-cancel-btn"
                  >
                    <X className="w-3.5 h-3.5" /> Abbrechen
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveEdits}
                    disabled={saving}
                    className="gap-1.5"
                    data-testid="detail-edit-save-btn"
                  >
                    {saving ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Save className="w-3.5 h-3.5" />
                    )}
                    Speichern
                  </Button>
                </div>
              )}
            </div>

            <dl className="space-y-4">
              <FieldRow
                label="Firmenname"
                value={editing ? form.business_name : customer.business_name}
                editing={editing}
                onChange={(v) => setForm((f) => ({ ...f, business_name: v }))}
                testId="detail-field-name"
              />
              <FieldRow
                label="Adresse"
                value={editing ? form.address : customer.address}
                editing={editing}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                testId="detail-field-address"
              />
              <FieldRow
                label="Place ID"
                value={customer.place_id || "—"}
                readOnly
                testId="detail-field-placeid"
              />
              <FieldRow
                label="Google-Bewertungslink"
                value={editing ? form.google_review_url : customer.google_review_url}
                editing={editing}
                onChange={(v) => setForm((f) => ({ ...f, google_review_url: v }))}
                testId="detail-field-review-url"
                mono
              />
              <FieldRow
                label="Slug"
                value={editing ? form.slug : customer.slug}
                editing={editing}
                onChange={(v) =>
                  setForm((f) => ({ ...f, slug: v.toLowerCase().replace(/\s+/g, "-") }))
                }
                testId="detail-field-slug"
                mono
              />
              <FieldRow
                label="NFC-Link"
                value={nfcLink}
                readOnly
                mono
                testId="detail-field-nfc"
              />
            </dl>
          </div>

          {/* Recent scans */}
          <div className="card-surface p-5" data-testid="detail-scans-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold">Letzte Scans</h3>
              <span className="text-xs text-muted-foreground">
                {scans.length} Ereignisse
              </span>
            </div>
            {scans.length ? (
              <ul className="divide-y divide-white/[0.05]">
                {scans.map((s) => (
                  <li
                    key={s.id}
                    className="py-3 flex items-center justify-between gap-3 text-sm"
                  >
                    <div>
                      <div className="font-medium">{fmt(s.timestamp)}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.device_type || "—"} · {s.browser || "—"} · {s.operating_system || "—"}
                      </div>
                    </div>
                    <Badge variant="outline" className="border-white/10 text-muted-foreground">
                      {s.device_type || "unbekannt"}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground border border-dashed border-white/[0.08] rounded-lg">
                Noch keine Scans erfasst.
              </div>
            )}
          </div>
        </div>

        {/* Right: QR */}
        <div className="space-y-6">
          <div className="card-surface p-5" id="qr" data-testid="detail-qr-card">
            <div className="flex items-center gap-2 mb-4">
              <QrCode className="w-4 h-4 text-primary" />
              <h3 className="font-heading text-lg font-semibold">QR-Code</h3>
            </div>
            <div className="bg-white rounded-xl p-5 flex items-center justify-center">
              <QRCodeCanvas
                value={nfcLink || "https://1ai-agency.com"}
                size={220}
                level="H"
                marginSize={2}
                fgColor="#0a0a0a"
                bgColor="#ffffff"
                data-testid="detail-qr-canvas"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-3 break-all">
              <LinkIcon className="w-3 h-3 inline mr-1" />
              {nfcLink}
            </p>
            <Separator className="my-4 bg-white/[0.06]" />
            <div className="flex flex-col gap-2">
              <Button
                onClick={downloadQr}
                className="w-full rounded-full gap-2"
                data-testid="detail-download-qr-btn"
              >
                <Download className="w-4 h-4" /> QR PNG herunterladen
              </Button>
              <Button
                onClick={copyLink}
                variant="secondary"
                className="w-full rounded-full gap-2"
                data-testid="detail-copy-nfc-btn-secondary"
              >
                <Copy className="w-4 h-4" /> Custom-Link kopieren
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* NFC how-to modal */}
      <Dialog open={nfcModalOpen} onOpenChange={setNfcModalOpen}>
        <DialogContent className="max-w-md bg-card border-white/[0.06]" data-testid="nfc-howto-modal">
          <DialogHeader>
            <DialogTitle>NFC-Chip beschreiben</DialogTitle>
            <DialogDescription>
              So schreibst du den Aufsteller-Link auf einen leeren NFC-Chip:
            </DialogDescription>
          </DialogHeader>
          <ol className="mt-2 space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs grid place-items-center font-semibold shrink-0">1</span>
              <span>NFC Tools öffnen</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs grid place-items-center font-semibold shrink-0">2</span>
              <span>Schreiben → Datensatz hinzufügen → <strong>URL/URI</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs grid place-items-center font-semibold shrink-0">3</span>
              <span>Kopierten Link einfügen und auf den Chip schreiben</span>
            </li>
          </ol>
          <div className="mt-4 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
            Chip erst nach erfolgreichem QR- und NFC-Test sperren.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SmallKpi({ label, value, icon: Icon, tone, small, testId }) {
  return (
    <div className="card-surface p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <Icon className={`w-4 h-4 ${tone === "primary" ? "text-primary" : "text-muted-foreground"}`} />
      </div>
      <div
        className={`mt-2 font-heading font-bold ${
          small ? "text-sm" : "text-2xl"
        } ${tone === "primary" ? "text-primary" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}

function FieldRow({ label, value, editing, onChange, readOnly, mono, testId }) {
  const showEdit = editing && !readOnly;
  return (
    <div className="grid sm:grid-cols-[180px,1fr] gap-2 sm:gap-4 items-start">
      <Label className="label-eyebrow pt-2">{label}</Label>
      {showEdit ? (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="bg-background/50 h-10"
          data-testid={testId}
        />
      ) : (
        <div
          className={`text-sm py-2 break-all ${mono ? "font-mono text-xs bg-white/[0.02] px-3 py-2 rounded border border-white/[0.05]" : ""}`}
          data-testid={testId}
        >
          {value || "—"}
        </div>
      )}
    </div>
  );
}


const LIFECYCLE_LABELS = {
  new: { label: "Neu", cls: "border-white/10 text-muted-foreground bg-white/[0.03]" },
  preparing: { label: "In Vorbereitung", cls: "border-amber-500/30 text-amber-400 bg-amber-500/10" },
  ready: { label: "Verkaufsbereit", cls: "border-primary/30 text-primary bg-primary/10" },
  delivered: { label: "Ausgeliefert", cls: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" },
};

function LifecycleBadge({ status, testId }) {
  const l = LIFECYCLE_LABELS[status] || LIFECYCLE_LABELS.new;
  return (
    <Badge variant="outline" className={l.cls} data-testid={testId || "lifecycle-badge"}>
      {l.label}
    </Badge>
  );
}

function ChecklistItem({ label, done, onChange, autoLocked, testId, icon: Icon }) {
  return (
    <li className="flex items-center gap-3">
      {autoLocked ? (
        <div
          className="h-4 w-4 rounded border border-emerald-500/40 bg-emerald-500/20 grid place-items-center shrink-0"
          data-testid={testId}
        >
          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
        </div>
      ) : (
        <Checkbox
          checked={done}
          onCheckedChange={(v) => onChange?.(!!v)}
          data-testid={testId}
        />
      )}
      <span className={`text-sm flex-1 ${done ? "line-through text-muted-foreground" : ""}`}>
        {label}
      </span>
      {Icon ? <Icon className={`w-3.5 h-3.5 ${done ? "text-emerald-400" : "text-muted-foreground/50"}`} />
        : done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
        : <Circle className="w-3.5 h-3.5 text-muted-foreground/50" />}
    </li>
  );
}

function ChecklistProgress({ cl }) {
  const total = 7;
  // 2 auto (customer_created, qr_generated) + 5 manual keys
  const manual = ["qr_downloaded", "nfc_copied", "qr_tested", "nfc_tested", "delivered"];
  const done = 2 + manual.filter((k) => !!cl?.[k]).length;
  return (
    <span
      className="text-[11px] font-mono text-muted-foreground tabular-nums"
      data-testid="checklist-progress"
    >
      {done}/{total}
    </span>
  );
}
